import {
    LEAD_DETAILS_SUCCESS,
    LEAD_DETAILS_FAILURE,
    LEAD_SET_LOADING,
    LEAD_SET_ERROR,
    LEAD_CLEAR_ERROR,
    LEAD_DETAILS_RESET,
    LEAD_ADD_REQUEST,
    LEAD_ADD_SUCCESS,
    LEAD_ADD_FAILURE,
    LEAD_ADD_RESET
} from "./LeadActionType";

/* ============================================================
   LEAD INTERFACES - EXACT MATCH TO YOUR BACKEND DTO
============================================================ */

const apiConfig = window as any;

/* ------------------- BASE API URL ------------------- */

const BASE_API_URL =
    apiConfig.BASE_API_URL?.charAt(apiConfig.BASE_API_URL.length - 1) === "/"
        ? apiConfig.BASE_API_URL.substring(0, apiConfig.BASE_API_URL.length - 1)
        : apiConfig.BASE_API_URL;

console.log("Employee API URL:", BASE_API_URL);

const BasicAuthentication = apiConfig.BasicAuthentication;

/* ------------------- HEADERS ------------------- */

const postAuthorizedHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(BasicAuthentication
        ? { Authorization: "Basic " + btoa(BasicAuthentication) }
        : {})
};

export interface LeadRecord {
    leadId: number | null;
    leadImage: string | null;
    clientName: string | null;
    clientParentName: string | null;
    clientEmail: string | null;
    clientPhone: string | null;
    leadSource: string | null;
    leadStatus: boolean | null;
    leadType: string | null;
    interestedCourse: string | null;
    preferredLocation: string | null;
    budget: number | null;
    preferredCollege1: string | null;
    preferredCollege2: string | null;
    preferredCollege3: string | null;
    followupTimeline: string | null;  // As VARCHAR
    nextAction: string | null;
    lastRemarks: string | null;
    bookingStatus: string | null;
    bookingType: string | null;  // New field: Type of booking (Site Visit, Virtual Meeting, etc.)
    expectedDate: string | null;  // New field: Expected date for the booking
    visitData: string | null;  // New field: Visit data/details
    createdAt: string | null;  // As VARCHAR
    updatedAt: string | null;  // As VARCHAR
}

export interface Employee {
    ID: number;
    EmployeeID: number;
    EmployeeCode: string;
    EmployeeName: string;
    Email: string;
    MobileNumber: string;
    Department: string;
    Designation: string;
    EmploymentStatus: string;
    IsActive: boolean;
}

export interface EmployeeInfo {
    employeeCode: string;
    employeeName: string;
    department: string;
    designation: string;
}

export interface LeadApiResponse {
    isSuccess: boolean;  // Note: isSuccess, not success
    message: string;
    records: LeadRecord[];
    emplopyelist: Employee[] | null;
    accessedOn: string | null;
}

/* ============================================================
   ADD LEAD API INTERFACES
============================================================ */

export interface AddLeadRequest {
    indicator: string;
    id: number | null;
    clientname: string;
    clientparentname: string;
    clientemail: string;
    clientphone: string;
    leadsource: string;
    leadstatus: boolean;
    leadtype: string;
    interestedcourse: string;
    preferredlocation: string;
    budget: number;
    preferredcollege1: string;
    preferredcollege2: string;
    preferredcollege3: string;
    followuptimeline: string;
    nextaction: string;
    lastremarks: string;
    bookingstatus: string;
    bookingtype: string;  // New field: Type of booking
    expecteddate: string;  // New field: Expected date
    visitdate: string;  // New field: Visit data
    employee_code: string;
}

export interface AddLeadResponse {
    Success: boolean;
    Message: string;
    ID: number;
    ClientName: string;
}

/* ============================================================
   ADD LEAD API ACTION - UPDATED VERSION
============================================================ */

export const AddLead = 
    (leadData: AddLeadRequest) => async (dispatch: any) => {
        
        console.log(`========== ACTION: AddLead ==========`);
        console.log("Adding new lead with data:", leadData);
        
        dispatch({ 
            type: LEAD_ADD_REQUEST, 
            payload: { 
                loading: true, 
                operation: leadData.indicator === 'I' ? 'INSERT' : leadData.indicator === 'U' ? 'UPDATE' : 'DELETE' 
            } 
        });

        try {
            const url = `${BASE_API_URL}/LeadUpdate`;

            console.log("Adding lead to:", url);

            const response = await fetch(url, {
                method: "POST",
                headers: postAuthorizedHeaders,
                body: JSON.stringify(leadData)
            });
            
            console.log("Add Lead Response status:", response.status);
            console.log("Add Lead Response status text:", response.statusText);

            // Try to get response as text first for debugging
            const responseText = await response.text();
            console.log("Raw response text:", responseText);

            let data;
            try {
                data = JSON.parse(responseText);
                console.log("Parsed response data:", data);
            } catch (parseError) {
                console.error("Failed to parse response as JSON:", parseError);
                
                const errorPayload = {
                    success: false,
                    message: "Invalid JSON response from server",
                    error: responseText,
                    operation: leadData.indicator === 'I' ? 'INSERT' : leadData.indicator === 'U' ? 'UPDATE' : 'DELETE'
                };
                
                dispatch({
                    type: LEAD_ADD_FAILURE,
                    payload: errorPayload
                });
                
                return errorPayload;
            }

            // Check if the response was successful
            if (response.ok && data && data.Success === true) {
                console.log(`✅ Lead added successfully with ID: ${data.ID}`);
                
                const successPayload = {
                    success: true,
                    message: data.Message || (leadData.indicator === 'I' ? 'Lead inserted successfully' : 
                                             leadData.indicator === 'U' ? 'Lead updated successfully' : 
                                             'Lead deleted successfully'),
                    id: data.ID,
                    clientName: data.ClientName,
                    response: data,
                    operation: leadData.indicator === 'I' ? 'INSERT' : 
                              leadData.indicator === 'U' ? 'UPDATE' : 'DELETE'
                };
                
                dispatch({
                    type: LEAD_ADD_SUCCESS,
                    payload: successPayload
                });

                // After successfully adding/updating/deleting a lead, refresh the leads list for this employee
                if (leadData.employee_code && leadData.indicator !== 'D') {
                    setTimeout(() => {
                        dispatch(GetLeadDetailsByEmployee(leadData.employee_code));
                    }, 500);
                } else if (leadData.employee_code && leadData.indicator === 'D') {
                    setTimeout(() => {
                        dispatch(GetLeadDetailsByEmployee(leadData.employee_code));
                    }, 500);
                }

                return successPayload;
            }
            // Handle error cases
            else {
                console.log("❌ API returned error:", data?.Message);
                
                const errorMessage = data?.Message || `HTTP Error ${response.status}: ${response.statusText}`;
                
                const errorPayload = {
                    success: false,
                    message: errorMessage,
                    error: data,
                    operation: leadData.indicator === 'I' ? 'INSERT' : 
                              leadData.indicator === 'U' ? 'UPDATE' : 'DELETE'
                };
                
                dispatch({
                    type: LEAD_ADD_FAILURE,
                    payload: errorPayload
                });

                // Also set error in error state
                dispatch({
                    type: LEAD_SET_ERROR,
                    payload: {
                        success: false,
                        message: errorMessage,
                        error: data
                    }
                });

                return errorPayload;
            }

        } catch (err) {
            console.error("AddLead error:", err);

            const errorMessage = err instanceof Error ? err.message : "Failed to add lead";

            const errorPayload = {
                success: false,
                message: errorMessage,
                error: err,
                operation: leadData.indicator === 'I' ? 'INSERT' : 
                          leadData.indicator === 'U' ? 'UPDATE' : 'DELETE'
            };

            dispatch({
                type: LEAD_ADD_FAILURE,
                payload: errorPayload
            });

            dispatch({
                type: LEAD_SET_ERROR,
                payload: {
                    success: false,
                    message: errorMessage,
                    error: err
                }
            });

            return errorPayload;
        }
    };

/* ============================================================
   GET LEAD DETAILS BY EMPLOYEE CODE
============================================================ */

export const GetLeadDetailsByEmployee = 
    (employeeCode: string) => async (dispatch: any) => {
        
        console.log(`========== ACTION: GetLeadDetailsByEmployee ==========`);
        console.log(`Fetching lead details for employee code: ${employeeCode}`);
        
        dispatch({ type: LEAD_SET_LOADING, payload: true });

        try {
            const params = new URLSearchParams();
            if (employeeCode) {
                params.append('employeeCode', employeeCode);
            }

            const queryString = params.toString();
            const url = `${BASE_API_URL}/LeadDetailsByEmp${queryString ? `?${queryString}` : ''}`;

            console.log("Fetching lead details from:", url);

            const response = await fetch(url, {
                method: "GET",
                headers: postAuthorizedHeaders
            });

            console.log("Response status:", response.status);
            console.log("Response headers:", response.headers);

            const data: LeadApiResponse = await response.json();
            
            console.log("RAW LEAD API RESPONSE:", data);
            console.log("Response type:", typeof data);
            console.log("isSuccess:", data?.isSuccess);
            console.log("Message:", data?.message);
            console.log("Records:", data?.records);
            console.log("Records length:", data?.records?.length);
            console.log("First record sample:", data?.records?.[0]);
            console.log("Employee list (emplopyelist):", data?.emplopyelist);
            console.log("Employee list length:", data?.emplopyelist?.length);
            
            // Check if emplopyelist exists and has data
            if (data?.emplopyelist && data.emplopyelist.length > 0) {
                console.log("Employee list details:", data.emplopyelist.map(emp => ({
                    code: emp.EmployeeCode,
                    name: emp.EmployeeName,
                    dept: emp.Department,
                    designation: emp.Designation
                })));
            } else {
                console.log("No employee list found in response or emplopyelist is empty/null");
            }

            dispatch({ type: LEAD_SET_LOADING, payload: false });

            // Always extract employee info if available, regardless of isSuccess
            let employeeInfo = null;
            let employeesList: Employee[] = [];
            
            if (data?.emplopyelist && data.emplopyelist.length > 0) {
                employeesList = data.emplopyelist;
                employeeInfo = {
                    employeeCode: data.emplopyelist[0].EmployeeCode,
                    employeeName: data.emplopyelist[0].EmployeeName,
                    department: data.emplopyelist[0].Department,
                    designation: data.emplopyelist[0].Designation
                };
                console.log("Extracted employee info:", employeeInfo);
                console.log("Extracted employees list:", employeesList.length, "employees");
            }

            // Check if the response has isSuccess and records array
            if (data && data.isSuccess === true && Array.isArray(data.records)) {
                console.log(`✅ API returned success with ${data.records.length} lead records`);
                
                dispatch({
                    type: LEAD_DETAILS_SUCCESS,
                    payload: {
                        records: data.records,
                        employeeInfo: employeeInfo,
                        employees: employeesList,
                        message: data.message
                    }
                });

                return { 
                    success: true, 
                    records: data.records,
                    employeeInfo: employeeInfo,
                    employees: employeesList,
                    message: data.message 
                };
            }
            // Handle empty records but successful response
            else if (data && data.isSuccess === true) {
                console.log("✅ API returned success but no records found");
                
                dispatch({
                    type: LEAD_DETAILS_SUCCESS,
                    payload: {
                        records: [],
                        employeeInfo: employeeInfo,
                        employees: employeesList,
                        message: data.message || "No leads found for this employee"
                    }
                });

                return { 
                    success: true, 
                    records: [],
                    employeeInfo: employeeInfo,
                    employees: employeesList,
                    message: data.message || "No leads found for this employee"
                };
            }
            // Handle error cases but still try to set employee list if available
            else {
                console.log("❌ API returned error:", data?.message);
                
                const errorMessage = data?.message || "Failed to fetch leads";
                
                // Even on error, if we have employee list, dispatch success with empty records
                if (employeesList.length > 0) {
                    console.log("Error occurred but employee list is available. Dispatching with empty records.");
                    dispatch({
                        type: LEAD_DETAILS_SUCCESS,
                        payload: {
                            records: [],
                            employeeInfo: employeeInfo,
                            employees: employeesList,
                            message: errorMessage
                        }
                    });
                    
                    return { 
                        success: false, 
                        records: [],
                        employeeInfo: employeeInfo,
                        employees: employeesList,
                        message: errorMessage 
                    };
                } else {
                    dispatch({
                        type: LEAD_DETAILS_FAILURE,
                        payload: errorMessage
                    });

                    dispatch({
                        type: LEAD_SET_ERROR,
                        payload: {
                            success: false,
                            message: errorMessage,
                            error: data
                        }
                    });

                    return { 
                        success: false, 
                        records: [],
                        employees: [],
                        message: errorMessage 
                    };
                }
            }

        } catch (err) {
            console.error("GetLeadDetailsByEmployee error:", err);

            dispatch({ type: LEAD_SET_LOADING, payload: false });

            const errorMessage = err instanceof Error ? err.message : "Failed to fetch leads";

            dispatch({
                type: LEAD_SET_ERROR,
                payload: {
                    success: false,
                    message: errorMessage,
                    error: err
                }
            });

            dispatch({
                type: LEAD_DETAILS_FAILURE,
                payload: errorMessage
            });

            return { 
                success: false, 
                records: [],
                employees: [],
                message: errorMessage,
                error: err 
            };
        }
    };

/* ============================================================
   RESET ADD LEAD STATE
============================================================ */

export const ResetAddLead = () => ({
    type: LEAD_ADD_RESET
});

/* ============================================================
   ADDITIONAL LEAD ACTIONS
============================================================ */

export const SetSelectedLead = (leadRecord: LeadRecord | null) => ({
    type: "LEAD_SET_SELECTED",
    payload: leadRecord
});

export const ClearLeadDetails = () => (dispatch: any) => {
    dispatch({ type: LEAD_DETAILS_RESET });
};

export const ClearLeadError = () => ({
    type: LEAD_CLEAR_ERROR
});

/* ============================================================
   LEAD FILTER ACTIONS (Optional - if needed)
============================================================ */

export interface LeadFilterRequest {
    leadSource?: string;
    leadStatus?: boolean;
    leadType?: string;
    interestedCourse?: string;
    bookingStatus?: string;
    bookingType?: string;  // New field: Filter by booking type
    fromDate?: string;
    toDate?: string;
}

export const SetLeadFilters = (filters: LeadFilterRequest) => ({
    type: "LEAD_SET_FILTERS",
    payload: filters
});

export const ClearLeadFilters = () => ({
    type: "LEAD_CLEAR_FILTERS"
});